import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  DollarSign, 
  LogOut,
  Users,
  AlertTriangle,
  Settings,
  ArrowLeft,
  Loader2,
  LayoutDashboard,
  Briefcase,
  Bot,
  FileText,
  CreditCard,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import { AGENT_CODES } from '../config/environment';
import type { Agent, Case, Debtor, PaymentPlan, Payment, Document, Communication, ExceptionRequest } from '../types';

interface CaseDetailPageProps {
  user: Agent;
  onLogout: () => void;
}

interface AgentExecution {
  code: string;
  name: string;
  description: string;
  isRunning: boolean;
  result?: string;
  error?: string;
}

export default function CaseDetailPage({ user, onLogout }: CaseDetailPageProps) {
  const { caseId } = useParams<{ caseId: string }>();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [debtor, setDebtor] = useState<Debtor | null>(null);
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [requests, setRequests] = useState<ExceptionRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'payments' | 'documents' | 'communications' | 'ai'>('overview');
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  
  const [agents, setAgents] = useState<AgentExecution[]>([
    { code: AGENT_CODES.SEGMENTADOR, name: 'Debtor Segmentation', description: 'Analyze debtor profile and update segment classification', isRunning: false },
    { code: AGENT_CODES.COMUNICADOR, name: 'Communication Generator', description: 'Generate personalized communication based on debtor profile', isRunning: false },
    { code: AGENT_CODES.ANALIZADOR, name: 'Document Analyzer', description: 'Analyze uploaded documents and extract key information', isRunning: false },
    { code: AGENT_CODES.EVALUADOR, name: 'Hardship Evaluator', description: 'Evaluate hardship requests and provide recommendations', isRunning: false },
    { code: AGENT_CODES.JUDICIAL, name: 'Legal Assessment', description: 'Assess case for potential legal action', isRunning: false },
    { code: AGENT_CODES.VALIDADOR, name: 'Payment Plan Validator', description: 'Validate and optimize payment plan proposals', isRunning: false },
  ]);

  useEffect(() => {
    if (caseId) {
      loadCaseData();
    }
  }, [caseId]);

  const loadCaseData = async () => {
    setIsLoading(true);
    try {
      // Load case
      const caseResult = await serenityClient.queryData<Case>(
        `SELECT * FROM Cases WHERE case_id = '${caseId}' LIMIT 1`
      );
      if (caseResult[0]) {
        setCaseData(caseResult[0]);
        
        // Load debtor
        const debtorResult = await serenityClient.queryData<Debtor>(
          `SELECT * FROM Debtors WHERE debtor_id = '${caseResult[0].debtor_id}' LIMIT 1`
        );
        setDebtor(debtorResult[0] || null);
      }

      // Load related data
      const [planResult, paymentsResult, docsResult, commsResult, reqsResult] = await Promise.all([
        serenityClient.queryData<PaymentPlan>(`SELECT * FROM PaymentPlans WHERE case_id = '${caseId}' ORDER BY created_at DESC LIMIT 1`),
        serenityClient.queryData<Payment>(`SELECT * FROM Payments WHERE case_id = '${caseId}' ORDER BY payment_date DESC`),
        serenityClient.queryData<Document>(`SELECT * FROM Documents WHERE case_id = '${caseId}' ORDER BY uploaded_at DESC`),
        serenityClient.queryData<Communication>(`SELECT * FROM Communications WHERE case_id = '${caseId}' ORDER BY sent_at DESC LIMIT 10`),
        serenityClient.queryData<ExceptionRequest>(`SELECT * FROM ExceptionRequests WHERE case_id = '${caseId}' ORDER BY created_at DESC`),
      ]);

      setPaymentPlan(planResult[0] || null);
      setPayments(paymentsResult);
      setDocuments(docsResult);
      setCommunications(commsResult);
      setRequests(reqsResult);
    } catch (error) {
      console.error('Failed to load case data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const runAgent = async (agentCode: string) => {
    setAgents(prev => prev.map(a => 
      a.code === agentCode ? { ...a, isRunning: true, result: undefined, error: undefined } : a
    ));

    try {
      const result = await serenityClient.executeAgentWithContext(agentCode, {
        caseId: caseId,
        debtorId: debtor?.debtor_id,
      });

      setAgents(prev => prev.map(a => 
        a.code === agentCode ? { ...a, isRunning: false, result: result.response } : a
      ));
    } catch (error) {
      setAgents(prev => prev.map(a => 
        a.code === agentCode ? { ...a, isRunning: false, error: 'Failed to execute agent' } : a
      ));
    }
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': case 'in_contact': return 'bg-blue-100 text-blue-800';
      case 'negotiating': case 'arrangement': return 'bg-yellow-100 text-yellow-800';
      case 'paying': return 'bg-green-100 text-green-800';
      case 'broken_arrangement': case 'pre_legal': case 'legal': return 'bg-red-100 text-red-800';
      case 'paid_in_full': case 'settled': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/cases', icon: Briefcase, label: 'Cases', active: true },
    { path: '/debtors', icon: Users, label: 'Debtors' },
    { path: '/requests', icon: AlertTriangle, label: 'Requests' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Case Not Found</h2>
          <Link to="/cases" className="text-blue-600 hover:underline">Back to Cases</Link>
        </div>
      </div>
    );
  }

  const remainingBalance = caseData.total_amount - (caseData.amount_paid || 0);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-semibold">DFS Collections</h1>
              <p className="text-xs text-slate-400">Agent Portal</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    item.active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-sm font-medium">
                {user.first_name[0]}{user.last_name[0]}
              </div>
              <div>
                <p className="text-sm font-medium">{user.first_name}</p>
                <p className="text-xs text-slate-400">{user.role}</p>
              </div>
            </div>
            <button onClick={onLogout} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Link to="/cases" className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{caseData.case_id}</h1>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(caseData.status)}`}>
                  {getStatusLabel(caseData.status)}
                </span>
              </div>
              <p className="text-gray-500">{caseData.origin} · {caseData.cedent}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(remainingBalance)}</p>
              <p className="text-sm text-gray-500">Remaining balance</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex gap-8">
              {(['overview', 'payments', 'documents', 'communications', 'ai'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'ai' ? 'AI Agents' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Case Info */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Case Details</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div><p className="text-sm text-gray-500">Original Amount</p><p className="font-semibold">{formatCurrency(caseData.original_amount)}</p></div>
                    <div><p className="text-sm text-gray-500">Total Amount</p><p className="font-semibold">{formatCurrency(caseData.total_amount)}</p></div>
                    <div><p className="text-sm text-gray-500">Amount Paid</p><p className="font-semibold text-green-600">{formatCurrency(caseData.amount_paid || 0)}</p></div>
                    <div><p className="text-sm text-gray-500">Days Overdue</p><p className="font-semibold text-red-600">{caseData.days_overdue} days</p></div>
                    <div><p className="text-sm text-gray-500">Product Type</p><p className="font-semibold">{caseData.product_type}</p></div>
                    <div><p className="text-sm text-gray-500">Priority</p><p className="font-semibold">{caseData.priority}</p></div>
                    <div><p className="text-sm text-gray-500">Created</p><p className="font-semibold">{formatDate(caseData.created_at)}</p></div>
                    <div><p className="text-sm text-gray-500">Assigned Agent</p><p className="font-semibold">{caseData.assigned_agent || 'Unassigned'}</p></div>
                  </div>
                </div>

                {/* Payment Plan */}
                {paymentPlan && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Active Payment Plan</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div><p className="text-sm text-gray-500">Plan Type</p><p className="font-semibold">{getStatusLabel(paymentPlan.plan_type)}</p></div>
                      <div><p className="text-sm text-gray-500">Installment</p><p className="font-semibold">{formatCurrency(paymentPlan.installment_amount || 0)}</p></div>
                      <div><p className="text-sm text-gray-500">Frequency</p><p className="font-semibold">{paymentPlan.installment_frequency}</p></div>
                      <div><p className="text-sm text-gray-500">Next Payment</p><p className="font-semibold">{paymentPlan.next_payment_date ? formatDate(paymentPlan.next_payment_date) : 'N/A'}</p></div>
                    </div>
                  </div>
                )}

                {/* Exception Requests */}
                {requests.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Exception Requests</h2>
                    <div className="space-y-3">
                      {requests.map((req) => (
                        <div key={req.request_id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{req.request_type}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              req.status === 'approved' ? 'bg-green-100 text-green-800' :
                              req.status === 'denied' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>{getStatusLabel(req.status)}</span>
                          </div>
                          <p className="text-sm text-gray-600">{req.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Debtor Info */}
              <div className="space-y-6">
                {debtor && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Debtor Information</h2>
                    <div className="space-y-3">
                      <div><p className="text-sm text-gray-500">Name</p><p className="font-semibold">{debtor.debtor_type === 'corporate' ? debtor.first_name : `${debtor.first_name} ${debtor.last_name}`}</p></div>
                      <div><p className="text-sm text-gray-500">Type</p><p className="font-semibold">{debtor.debtor_type}</p></div>
                      <div><p className="text-sm text-gray-500">Email</p><p className="font-semibold">{debtor.email}</p></div>
                      <div><p className="text-sm text-gray-500">Phone</p><p className="font-semibold">{debtor.phone || 'N/A'}</p></div>
                      <div><p className="text-sm text-gray-500">Segment</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          debtor.segment === 'high' ? 'bg-green-100 text-green-800' :
                          debtor.segment === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>{debtor.segment}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h2>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No payments recorded.</div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div key={payment.payment_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {payment.status === 'completed' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                         payment.status === 'failed' ? <XCircle className="w-5 h-5 text-red-600" /> :
                         <Clock className="w-5 h-5 text-yellow-600" />}
                        <div>
                          <p className="font-medium">{formatCurrency(payment.amount)}</p>
                          <p className="text-sm text-gray-500">{formatDate(payment.payment_date)} · {payment.payment_method}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                        payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>{payment.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No documents uploaded.</div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.document_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium">{doc.document_name}</p>
                          <p className="text-sm text-gray-500">{doc.document_type} · {formatDate(doc.uploaded_at)}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                        doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>{getStatusLabel(doc.status)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'communications' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Communication History</h2>
              {communications.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No communications recorded.</div>
              ) : (
                <div className="space-y-3">
                  {communications.map((comm) => (
                    <div key={comm.communication_id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-blue-600" />
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            comm.channel === 'email' ? 'bg-blue-100 text-blue-700' :
                            comm.channel === 'sms' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>{comm.channel.toUpperCase()}</span>
                          <span className="text-sm text-gray-500">{comm.direction}</span>
                        </div>
                        <span className="text-sm text-gray-500">{formatDate(comm.sent_at)}</span>
                      </div>
                      {comm.subject && <p className="font-medium text-gray-900">{comm.subject}</p>}
                      <p className="text-sm text-gray-600 mt-1">{comm.content.substring(0, 200)}...</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <Bot className="w-8 h-8" />
                  <h2 className="text-xl font-semibold">AI Agent Workbench</h2>
                </div>
                <p className="text-purple-100">
                  Execute AI agents to analyze this case, generate communications, evaluate requests, and more.
                </p>
              </div>

              <div className="space-y-4">
                {agents.map((agent) => (
                  <div key={agent.code} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedAgent(expandedAgent === agent.code ? null : agent.code)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Bot className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{agent.name}</p>
                          <p className="text-sm text-gray-500">{agent.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); runAgent(agent.code); }}
                          disabled={agent.isRunning}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {agent.isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                          {agent.isRunning ? 'Running...' : 'Execute'}
                        </button>
                        {expandedAgent === agent.code ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>
                    
                    {expandedAgent === agent.code && (agent.result || agent.error) && (
                      <div className="border-t border-gray-200 p-4 bg-gray-50">
                        {agent.error ? (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                            {agent.error}
                          </div>
                        ) : (
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-sm font-medium text-green-800 mb-2">Result:</p>
                            <pre className="text-sm text-green-700 whitespace-pre-wrap">{agent.result}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
